import { useCallback, useEffect, useState } from 'react'
import ReactPaginate from 'react-paginate'

import { PageTitle } from '../../../../../_metronic/layout/core'
import { getVirtualMerchantTickets, VirtualMerchantSearchField } from '../../core/_requests'
import { FilterManager, IBaseFilters } from '../../../../common/filter/FilterManager'
import { isTicketToken } from '../../../../../utils'
import { Table } from '../../../../../components/Table'
import { TableHead } from '../../../../../components/Table/TableHead'
import { TableBody } from '../../../../../components/Table/TableBody'
import VirtualMerchantItem from './VirtualMerchantItem'

import '../../../../assets/css/pagination.css'

const perPage = 20

const DEFAULT_SEARCH_FIELD: VirtualMerchantSearchField = 'matchingTicketToken'

const SEARCH_FIELD_OPTIONS: Array<{ value: VirtualMerchantSearchField; label: string }> = [
  { value: 'matchingTicketToken', label: 'Токен исходной выплаты (мэтчинг)' },
  { value: 'paymentTicketToken', label: 'Токен пополнения' },
  { value: 'virtualPayoutTicketToken', label: 'Токен выплаты виртуального мерчанта' },
]

const DEFAULT_FILTERS: IBaseFilters = {
  searchAdvanced: { field: DEFAULT_SEARCH_FIELD, query: '' },
}

const INVALID_TOKEN_TEXT = 'Некорректный токен'

interface AppliedSearch {
  field: VirtualMerchantSearchField
  token: string
}

const VirtualMerchant = () => {
  const [tickets, setTickets] = useState<
    Array<{
      paymentTicketToken: string
      matchingTicketToken: string
      virtualPayoutTicketToken: string
      amount: number
      createdAt: string
    }>
  >([])
  const [countPages, setCountPages] = useState(0)
  const [fullCount, setFullCount] = useState(0)
  const [isLoading, setLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC')
  const [appliedSearch, setAppliedSearch] = useState<AppliedSearch | null>(null)
  const [searchError, setSearchError] = useState('')

  const loadTicketList = useCallback(
    async (page: number, order: 'ASC' | 'DESC', search: AppliedSearch | null) => {
      setLoading(true)
      try {
        const response = await getVirtualMerchantTickets({
          page,
          perPage,
          sortBy: 'createdAt',
          sortDirection: order,
          ...(search ? { [search.field]: search.token } : {}),
        })

        if (!response?.data?.success) {
          throw new Error('Failed to load tickets')
        }

        const list = response.data.list || []
        const totalRows = response.data.totalRows || 0
        const pages = Math.ceil(totalRows / perPage)

        setTickets(list)
        setCountPages(pages)
        setFullCount(totalRows)
      } catch (error) {
        console.error('Ошибка при загрузке тикетов:', error)
        setTickets([])
        setCountPages(0)
        setFullCount(0)
      } finally {
        setLoading(false)
      }
    },
    []
  )

  useEffect(() => {
    loadTicketList(currentPage, sortOrder, appliedSearch)
  }, [currentPage, sortOrder, appliedSearch, loadTicketList])

  const handlePageChange = (selected: { selected: number }) => {
    setCurrentPage(selected.selected + 1)
  }

  const handleSortToggle = () => {
    setSortOrder((prev) => (prev === 'ASC' ? 'DESC' : 'ASC'))
  }

  const handleApplyFilters = (filters: IBaseFilters) => {
    const token = filters.searchAdvanced?.query?.trim() ?? ''
    const field = (filters.searchAdvanced?.field ?? DEFAULT_SEARCH_FIELD) as VirtualMerchantSearchField

    if (!token) {
      setSearchError('')
      setAppliedSearch(null)
      setCurrentPage(1)
      return
    }

    // An invalid value never reaches the list: the table and the applied search stay as they are.
    if (!isTicketToken(token)) {
      setSearchError(INVALID_TOKEN_TEXT)
      return
    }

    setSearchError('')
    setAppliedSearch({ field, token })
    setCurrentPage(1)
  }

  return (
    <div>
      <FilterManager
        activeFilters={['searchAdvanced']}
        defaultValues={DEFAULT_FILTERS}
        filterProps={{
          searchAdvanced: {
            options: SEARCH_FIELD_OPTIONS,
            label: 'Поиск по токену',
            placeholder: 'Вставьте токен',
            error: searchError,
          },
        }}
        onApplyFilters={handleApplyFilters}
      />
      <div className='card mb-7'>
        <PageTitle breadcrumbs={[]}>Виртуальный мерчант</PageTitle>
        <div className='card-header border-0 pt-5'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bold fs-3 mb-1'>Виртуальный мерчант</span>
            <span className='text-muted mt-1 fw-semibold fs-7'>Всего: {fullCount}</span>
          </h3>
        </div>

        <div className='card-body py-3'>
          <Table>
            <TableHead>
              <tr>
                <th className='min-w-300px'>
                  Мэтчинг → Пополнение → Выплата
                  <br />
                  <small className='text-muted'>Токены UUID</small>
                </th>
                <th className='min-w-150px'>Сумма пополнения/выплаты</th>
                <th className='min-w-150px'>
                  Дата создания
                  <button
                    onClick={handleSortToggle}
                    className='btn btn-sm btn-light-primary ms-2'
                    title={`Сортировка: ${sortOrder === 'ASC' ? 'по возрастанию' : 'по убыванию'}`}
                  >
                    {sortOrder === 'ASC' ? '↑' : '↓'}
                  </button>
                </th>
              </tr>
            </TableHead>
            <TableBody>
              {tickets.length > 0 ? (
                tickets.map((ticket, index) => (
                  <VirtualMerchantItem key={`${ticket.matchingTicketToken}-${index}`} ticket={ticket} />
                ))
              ) : (
                <tr>
                  <td colSpan={3} className='text-center'>
                    {isLoading ? 'Загрузка...' : 'Тикеты не найдены'}
                  </td>
                </tr>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {countPages > 1 && (
        <div className='d-flex justify-content-center mt-5'>
          <ReactPaginate
            breakLabel='...'
            nextLabel='Следующая >'
            onPageChange={handlePageChange}
            pageRangeDisplayed={5}
            pageCount={countPages}
            previousLabel='< Предыдущая'
            forcePage={currentPage - 1}
            renderOnZeroPageCount={() => null}
            className='pagination'
            activeClassName='active'
            disabledClassName='disabled'
            nextClassName='page-item'
            nextLinkClassName='page-link'
            pageClassName='page-item'
            pageLinkClassName='page-link'
            previousClassName='page-item'
            previousLinkClassName='page-link'
            breakClassName='page-item'
            breakLinkClassName='page-link'
          />
        </div>
      )}
    </div>
  )
}

export default VirtualMerchant

